// Selectors.
import Selectors from '../../selectors.js';

import _ from 'lodash';

// Config.
import Config from '../../config/config.js';
import Enums from '../../config/enums.js';

// Factories.
import { createDisplayItemEntityFromJSON } from '../../../../factories/displayItem.js';

import { clean } from '../../../../common/utils/object-utils';

// Misc dependencies.
import ConfigUtil from '../../utils/config.js';

/**
 * Items business logic.
 *
 * @namespace
 *
 * @memberof module:UgcWall.Model.Logic
 */
const Items = {
  /**
   * Builds params object to include with the request to the endpoint to fetch a set of items.
   *
   */
  buildFetchParams: function(
    componentType,
    phase,
    overridePhase,
    paging,
    sorting,
    filtering,
    showMoreMode,
    scope,
    displayStrategy,
    socialVoting,
    params = {},
    legacyMode = false,
    contentServicesVersion = 'v1',
    campaignId = null,
    collectionId = null,
    shouldShowStickyContent,
    beforeScore = null
  ) {
    let mergedParams = {};

    // - - - - - - - - - - - - - - - - - -
    // Get base request params.
    const baseParams = getBaseRequestParams(
      componentType,
      phase,
      overridePhase,
      paging,
      sorting,
      filtering,
      showMoreMode,
      scope,
      displayStrategy,
      socialVoting,
      legacyMode,
      contentServicesVersion,
      campaignId,
      collectionId,
      shouldShowStickyContent,
      beforeScore
    );

    // - - - - - - - - - - - - - - - - - -
    // Combine with params unique to this action.
    mergedParams = Object.assign(baseParams, params);

    return mergedParams;
  },

  /**
   * Builds override params for a paging request.
   *
   */
  buildPagingOverrideParams: function(paging, showMoreMode, direction) {
    const params = {
      showMoreMode: showMoreMode,
    };

    // Calculate new offset.
    switch (direction) {
      case 'prev':
        params.offset = paging.offset - paging.pageSize;
        break;

      case 'next':
        params.offset = paging.offset + paging.pageSize;
        break;
    }

    return params;
  },

  /**
   * Builds override params for a paging request.
   *
   */
  buildSortingOverrideParams: function(paging, sortBy, sortDir, totalItems) {
    const params = {
      sort:  sortBy,
      order: sortDir,

      // Request a page size equal to the current number of displayed items.
      max:    totalItems > paging.pageSize ? totalItems : paging.pageSize,
      offset: 0,

      // Always replace when sorting.
      showMoreMode: Enums.SHOW_MORE_MODE_REPLACE,
    };

    return params;
  },

  /**
   * Builds override params for a filtering request.
   *
   */
  buildFilteringOverrideParams: function(paging, filters, displayCategory, totalItems) {
    const params = {
      // Request a page size equal to the current number of displayed items.
      max:    totalItems > paging.pageSize ? totalItems : paging.pageSize,
      offset: 0,

      // Always replace when filtering.
      showMoreMode: Enums.SHOW_MORE_MODE_REPLACE,
    };

    // Override filtering params.
    if (filters.network != null) {
      params.externalService = filters.network !== 'all' ? filters.network : '';
    }

    if (!!displayCategory) {
      params.displayCategory = displayCategory;
    }

    if (!!filters.tag && filters.tag !== 'all') {
      params.displayCategory = !!params.displayCategory ? [params.displayCategory, filters.tag].join(',') : filters.tag;
    }

    if (!!filters.tag && filters.tag === 'all') {
      params.displayCategory = !!params.displayCategory ? params.displayCategory : '';
    }

    if (filters.user != null) {
      params.filterUser = filters.user;
    }

    if (filters.type != null) {
      params.contentType = filters.type !== 'all' ? filters.type : '';
    }

    if (filters.date != null) {
      params.filterDate = filters.date;
    }

    if (filters.filter1 != null) {
      params.filter1 = filters.filter1;
    }
    if (filters.filter2 != null) {
      params.filter2 = filters.filter2;
    }
    if (filters.filter3 != null) {
      params.filter3 = filters.filter3;
    }

    return params;
  },

  /**
   * Builds params object to include with the request to the endpoint to fetch a set of items.
   *
   */
  buildDisplayItemsFromResponse: function(responseContent) {
    // Create display item entity instances from the returned JSON.
    const displayItems = [];
    for (const displayItemKey in responseContent) {
      const displayItemJSON = responseContent[displayItemKey];
      const displayItem = createDisplayItemEntityFromJSON(displayItemJSON);
      displayItems.push(displayItem);
    }

    return displayItems;
  },
};

export default Items;

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Private internal functions/logic */

/**
 * Utility method to return common request params.
 *
 * @private
 *
 * @param componentType 		{string} The component type (entries/socialwall etc)
 * @param phase 			{object} State of campaign phases.
 * @param overridePhase 			{object} Phase to force.
 * @param paging 					{object} Paging data.
 * @param sorting 					{object} Sorting data.
 * @param filtering 				{object} Filtering data.
 * @param showMoreMode 				{string} Replace/append items on existing list.
 * @param scope 					{string} Item scope (campaign, global, or combined).
 * @param displayStrategy 			{string} Item prominence (Feature, show, spotlight etc).
 * @param socialVoting 				{boolean} Flag indicating if this module is in 'social' voting mode (vs entries voting).
 * @param legacyMode 		{boolean} Flag indicating if content services legacy mode is required for this instance type.
 * @param contentServicesVersion 	{string} The content services version. (v1, v2)
 * @param campaignId 				{string} The string id of the collection (for Content Services < 2).
 * @param collectionId 				{string} The string id of the collection (for Content Services >= 2).
 * @return {object} The object containing any common base params for requests to the server endpoint.
 */
function getBaseRequestParams(
  componentType,
  phase,
  overridePhase,
  paging,
  sorting,
  filtering,
  showMoreMode,
  scope,
  displayStrategy,
  socialVoting,
  legacyMode,
  contentServicesVersion,
  campaignId,
  collectionId,
  shouldShowStickyContent,
  beforeScore
) {
  let baseParams = {
    // Paging params.
    max:    paging.pageSize,
    offset: paging.offset,

    // Sorting params.
    sort:  sorting.sortBy,
    order: sorting.sortDir,

    // Filtering params.
    displayCategory: filtering.tag, // tag
    externalService: filtering.network, // network
    filterUser:      filtering.user,
    filterDate:      filtering.date,
    contentType:     filtering.type,

    // User captured filters (via entry form).
    filter1: filtering.entry.filter1,
    filter2: filtering.entry.filter2,
    filter3: filtering.entry.filter3,

    // Prominence (Feature, show, spotlight etc)
    displayStrategy: displayStrategy,
  };

  // If random sort, then also send a random salt value for backend to consistently order
  // a randomised result set.
  if (sorting.sortBy === 'random') {
    baseParams.randomsalt = sorting.sortRandomSalt;
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Define params specific to Content Service versions:

  // Version 1 (legacy)
  const v1Params = {
    // Scope (campaign, global, or combined)
    scope: scope,
    cID:   campaignId,
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Version 2.
  const v2Params = {
    // Collection id.
    collectionId: collectionId,
    pinned:       shouldShowStickyContent, // pinned means sticky here (as opposed to collage pinned content)
    beforeScore:  beforeScore,
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Additional contest phase params.
  const entryPhaseParams = {};
  const finalistPhaseParams = {
    winnerStatus: 'fn',
  };
  const resultsPhaseParams = {
    distinctWinnerStatus: 'fn',
    existsFieldName:      'winnerStatus',
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Switch based on content service version.
  switch (contentServicesVersion) {
    // Version 1 (legacy).
    case 'v1':
      baseParams = Object.assign(baseParams, v1Params);

      break;

    // Version 2.
    case 'v2':
      // legacy param support within v2
      if (legacyMode) {
        // Entries should always set collectionId of the experience collection
        // Other UGC walls should respect scope settings
        if (componentType === 'entries' || scope === 'combined' || scope === 'campaign') {
          baseParams = Object.assign(baseParams, { collectionId: collectionId });
        }
        if (scope === 'combined' || scope === 'global') {
          baseParams = Object.assign(baseParams, { scope: 'global' });
        }
        // for rare case of downgrade, collage would not have scope
        if (!scope) {
          baseParams = Object.assign(baseParams, { collectionId: collectionId });
        }
      } else {
        baseParams = Object.assign(baseParams, v2Params);
      }

      // Merge contest phase params (if required).
      if (phase) {
        // Results phase.
        if (phase.results || overridePhase === 'results') {
          baseParams = Object.assign(baseParams, resultsPhaseParams);

          // Finalist phase.
        } else if (phase.finalist || overridePhase === 'finalist') {
          baseParams = Object.assign(baseParams, finalistPhaseParams);

          // Entry phase.
        } else if (phase.entry || phase.voting || phase.judging || overridePhase === 'entry') {
          baseParams = Object.assign(baseParams, entryPhaseParams);
        }
      }

      break;
  }

  // Additional new parameter required by social voting to return the correct vote count.
  if (socialVoting) {
    baseParams.socialVoting = true;
  }
  //  removing undefined props
  clean(baseParams);

  return baseParams;
}
